import { PageBreadcrumbsHeader } from "pages/components/PageBreadcrumbsHeader/";
import { useGo, useNavigation, useTranslate } from "@refinedev/core";
import { Flex, Spin } from "antd";
import { ActionButton } from "pages/components/ActionButton/";
import { CheckIcon } from "@phosphor-icons/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useExistingContact } from "./hooks/useExistingContact";
import { AvatarDetails } from "./AvatarDetails";
import { AvatarSummary } from "./AvatarSummary";
import { useAntTheme } from "hooks/useAntTheme/";

export const AvatarEdit = ({ id }: { id?: string }) => {
  const t = useTranslate();
  const { list } = useNavigation();
  const go = useGo();
  const { isMobile } = useAntTheme();
  const isNewAvatar = !id;
  const hideAvatarSummary = isNewAvatar || isMobile;
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(!isNewAvatar);
  const [saving, setSaving] = useState(false);
  const saveCallback = useRef<() => Promise<void>>();

  const { data: existingContact, isLoading: isLoadingExistingContact } =
    useExistingContact(id);

  const spaceBetweenHeaderAndCard = useMemo(
    () => (isMobile ? 15 : 40),
    [isMobile]
  );

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      await saveCallback.current?.();
      setSaving(false);
      go?.({
        to: `../`,
      });
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  }, [go]);

  return (
    <>
      <PageBreadcrumbsHeader
        title={
          isNewAvatar
            ? t("components.ContactWithPresenter.header.createTitle")
            : t("components.ContactWithPresenter.header.editTitle")
        }
        subtitle={
          isMobile
            ? undefined
            : t("components.ContactWithPresenter.header.subtitle")
        }
      />
      <Flex gap={20} align="flex-start" style={{ width: "100%", marginTop: spaceBetweenHeaderAndCard }}>
        {isLoadingExistingContact ? (
          <Flex style={{ margin: "auto" }} justify="center" align="center">
            <Spin size="large" />
          </Flex>
        ) : (
          <>
            <AvatarDetails
              id={id}
              contact={existingContact?.contact?.data}
              presenter={{ data: existingContact?.presenter?.data ?? null }}
              setSubmitButtonEnabled={setSubmitButtonEnabled}
              saveCallback={saveCallback}
              style={{ width: hideAvatarSummary ? "100%" : "70%" }}
            />
            {!hideAvatarSummary && (
              <AvatarSummary
                contact={existingContact?.contact?.data}
                presenter={existingContact?.presenter?.data}
              />
            )}
          </>
        )}
      </Flex>
      <Flex
        gap={10}
        align="center"
        justify="space-between"
        style={{ padding: "24px 0" }}
      >
        <ActionButton
          onClick={() => list("media_contacts")}
          iconPosition="start"
          disabled={saving}
          style={{
            maxWidth: "50%",
            height: 50,
          }}
        >
          {t("buttons.back")}
        </ActionButton>
        <ActionButton
          type="primary"
          size="small"
          loading={saving}
          disabled={!submitButtonEnabled}
          style={{
            maxWidth: "50%",
            height: 50,
          }}
          icon={<CheckIcon />}
          onClick={handleSave}
        >
          {t(
            `components.ContactWithPresenter.AvatarEdit.${id ? "saveAvatar" : "createAvatar"}`
          )}
        </ActionButton>
      </Flex>
    </>
  );
};
